<template>
  <div class="relative">
    <!-- Title -->
    <div class="title">
      <h1>Cotizador de envíos</h1>
    </div>

    <div class="max-w-2xl mx-auto px-4 py-6">
      <!-- District selector -->
      <div class="form-group mb-6">
        <label class="block text-sm font-semibold text-gray-700 mb-2">Distrito de entrega</label>
        <select v-model="selectedDistrict" class="select">
          <option v-for="(d, i) in districts" :key="i" :value="d">{{ d.label }} - S/. {{ formatPrice(d.price) }}</option>
        </select>
      </div>

      <!-- Dimension inputs -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <!-- Ancho -->
        <div class="flex flex-col items-center gap-2">
          <div class="dimension-icon">
            <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-16 h-16">
              <rect x="20" y="25" width="40" height="30" rx="2" fill="#C8604A" stroke="#9B3D2A" stroke-width="1.5"/>
              <rect x="20" y="25" width="40" height="8" rx="2" fill="#A84A35" stroke="#9B3D2A" stroke-width="1.5"/>
              <line x1="20" y1="62" x2="60" y2="62" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
              <polygon points="20,59 14,62 20,65" fill="#555"/>
              <polygon points="60,59 66,62 60,65" fill="#555"/>
              <text x="40" y="75" text-anchor="middle" font-size="9" fill="#555" font-family="sans-serif">ANCHO</text>
            </svg>
          </div>
          <input
            v-model.number="form.width"
            type="number"
            min="0"
            placeholder="cm"
            class="input text-center"
          />
          <span class="text-xs text-gray-500">Ancho (cm)</span>
        </div>

        <!-- Largo -->
        <div class="flex flex-col items-center gap-2">
          <div class="dimension-icon">
            <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-16 h-16">
              <rect x="20" y="25" width="40" height="30" rx="2" fill="#C8604A" stroke="#9B3D2A" stroke-width="1.5"/>
              <rect x="20" y="25" width="40" height="8" rx="2" fill="#A84A35" stroke="#9B3D2A" stroke-width="1.5"/>
              <line x1="10" y1="40" x2="10" y2="55" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
              <polygon points="7,40 10,34 13,40" fill="#555"/>
              <polygon points="7,55 10,61 13,55" fill="#555"/>
              <text x="40" y="75" text-anchor="middle" font-size="9" fill="#555" font-family="sans-serif">LARGO</text>
            </svg>
          </div>
          <input
            v-model.number="form.depth"
            type="number"
            min="0"
            placeholder="cm"
            class="input text-center"
          />
          <span class="text-xs text-gray-500">Largo (cm)</span>
        </div>

        <!-- Alto -->
        <div class="flex flex-col items-center gap-2">
          <div class="dimension-icon">
            <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-16 h-16">
              <rect x="20" y="20" width="40" height="35" rx="2" fill="#C8604A" stroke="#9B3D2A" stroke-width="1.5"/>
              <rect x="20" y="20" width="40" height="9" rx="2" fill="#A84A35" stroke="#9B3D2A" stroke-width="1.5"/>
              <line x1="68" y1="20" x2="68" y2="55" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
              <polygon points="65,20 68,14 71,20" fill="#555"/>
              <polygon points="65,55 68,61 71,55" fill="#555"/>
              <text x="40" y="75" text-anchor="middle" font-size="9" fill="#555" font-family="sans-serif">ALTO</text>
            </svg>
          </div>
          <input
            v-model.number="form.height"
            type="number"
            min="0"
            placeholder="cm"
            class="input text-center"
          />
          <span class="text-xs text-gray-500">Alto (cm)</span>
        </div>

        <!-- Peso -->
        <div class="flex flex-col items-center gap-2">
          <div class="dimension-icon">
            <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-16 h-16">
              <rect x="12" y="48" width="56" height="14" rx="3" fill="#C8604A" stroke="#9B3D2A" stroke-width="1.5"/>
              <rect x="16" y="44" width="48" height="8" rx="2" fill="#A84A35" stroke="#9B3D2A" stroke-width="1.5"/>
              <circle cx="40" cy="32" r="16" fill="white" stroke="#9B3D2A" stroke-width="1.5"/>
              <circle cx="40" cy="32" r="12" fill="none" stroke="#ddd" stroke-width="1"/>
              <line x1="40" y1="32" x2="40" y2="22" stroke="#C8604A" stroke-width="2" stroke-linecap="round"/>
              <circle cx="40" cy="32" r="2" fill="#9B3D2A"/>
              <text x="40" y="75" text-anchor="middle" font-size="9" fill="#555" font-family="sans-serif">PESO</text>
            </svg>
          </div>
          <input
            v-model.number="form.weight"
            type="number"
            min="0"
            step="0.1"
            placeholder="kg"
            class="input text-center"
          />
          <span class="text-xs text-gray-500">Peso (kg)</span>
        </div>
      </div>

      <!-- Calculate button -->
      <div class="flex justify-center mb-6">
        <button @click="calculate" class="btn px-10 text-sm font-bold tracking-widest uppercase inline-flex items-center">
          Calcular
        </button>
      </div>

      <!-- Result -->
      <div v-if="result !== null" class="flex justify-center mb-6">
        <div class="text-center">
          <span class="text-5xl font-bold text-secondary">S/ {{ formatPrice(result.total) }}</span>
          <p class="text-sm text-gray-500 mt-1">Costo total de envío</p>
          <div v-if="result.extraCost > 0" class="mt-3 text-xs text-gray-600 space-y-1">
            <div class="flex justify-between gap-6">
              <span>Tarifa base ({{ selectedDistrict.label }}):</span>
              <span class="font-semibold">S/ {{ formatPrice(selectedDistrict.price) }}</span>
            </div>
            <div class="flex justify-between gap-6">
              <span>Excedente ({{ result.shipping.caseType }}):</span>
              <span class="font-semibold">S/ {{ formatPrice(result.extraCost) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Warning notice -->
      <div class="bg-yellow-100 border border-yellow-300 rounded-lg px-5 py-4 text-sm text-yellow-900 text-center leading-relaxed">
        <strong>! Importante:</strong> La <strong>medida máxima</strong> permitida es de
        <strong>30 cm &times; 25 cm &times; 15 cm</strong> y el <strong>peso máximo</strong> es de <strong>1.5 kg</strong>.
        Enwam <strong>no se responsabiliza</strong> por daños, retrasos o costos adicionales si el paquete excede
        estas dimensiones o peso, o si la información registrada no corresponde al paquete real al momento del recojo.
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase/firebaseConfig'

export default {
  name: 'ShippingCalculator',
  data () {
    return {
      form: {
        width: 30,
        height: 25,
        depth: 15,
        weight: 1.5
      },
      result: null,
      selectedDistrict: null,
      districts: []
    }
  },
  async created () {
    const snap = await db.collection('districts').orderBy('sortOrder', 'asc').get()
    this.districts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    if (this.districts.length) {
      this.selectedDistrict = this.districts[0]
    }
  },
  methods: {
    formatPrice (value) {
      return parseFloat(value.toFixed(2))
    },
    calculate () {
      const shipping = this.calculateShipping({
        width: this.form.width,
        height: this.form.height,
        depth: this.form.depth,
        weight: this.form.weight
      })
      const extraCost = shipping.cost
      this.result = {
        shipping,
        extraCost,
        total: this.selectedDistrict.price + extraCost
      }
    },
    calculateShipping ({ width, height, depth, weight }) {
      width = Number(width) || 0
      height = Number(height) || 0
      depth = Number(depth) || 0
      weight = Number(weight) || 0

      const maxDimensions = { width: 30, height: 25, depth: 15 }
      const maxWeight = 1.5
      const divisor = 6000
      const costPerKg = 1.5

      const volumetricWeight = (width * height * depth) / divisor

      const exceedsDimensions =
        width > maxDimensions.width ||
        height > maxDimensions.height ||
        depth > maxDimensions.depth

      const exceedsWeight = weight > maxWeight

      let chargeableWeight = 0
      let caseType = 'Normal'

      if (exceedsDimensions && !exceedsWeight) {
        chargeableWeight = volumetricWeight - maxWeight
        caseType = 'Exceso de volumen'
      } else if (!exceedsDimensions && exceedsWeight) {
        chargeableWeight = weight - maxWeight
        caseType = 'Sobrepeso'
      } else if (exceedsDimensions && exceedsWeight) {
        chargeableWeight = Math.max(weight, volumetricWeight) - maxWeight
        caseType = 'Volumen + Sobrepeso'
      }

      const cost = chargeableWeight > 0 ? chargeableWeight * costPerKg : 0

      return {
        caseType,
        volumetricWeight: Number(volumetricWeight.toFixed(2)),
        chargeableWeight: Number(chargeableWeight.toFixed(2)),
        cost: Number(cost.toFixed(2))
      }
    }
  }
}
</script>

<style scoped>
.dimension-icon {
  @apply flex items-center justify-center;
}
</style>
